import Cookies from "js-cookie";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosInstance";
import useAlert from "../../hooks/Alert";
import bg_circles from "../../images/bg-circle.svg";
import back_arrow from "../../images/icons/material-symbols_arrow-back-ios-new.svg";
import AnimatedPage from "../FramerMotion/AnimatedPage";
import Alert from "./Alert/Alert";

const formatCurrentDatetime = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().replace("T", " ").split(".")[0];
    return encodeURIComponent(formattedDate);
};

function ForgotPassword({ baseURL, clientID, appSettings }) {
    const navigate = useNavigate();
    const { showAlert, setShowAlert, alertStyle, setAlertStyle, alertMessage, setAlertMessage, hideAlert } = useAlert();
    const emailInput = useRef();
    const mobileInput = useRef();
    const [useMobile, setUseMobile] = useState(true); // Toggle state
    const [loading, setLoading] = useState(false); // Loading state

    const newErrors = {};
    const sendPasswordReset = async () => {
        const inputRef = useMobile ? mobileInput : emailInput;

        // Ensure the inputRef is valid
        const phoneValue = inputRef?.current?.value?.trim() || "";
        const prefix = String(appSettings?.SMS_SETTINGS?.ALLOWED_PREFIX || "");
        const mobileLength = Number(appSettings?.Mobile_length || 0);
        const expectedLen = prefix.length + mobileLength;

        if (useMobile && !phoneValue) {
            newErrors.phoneNumber = "Phone number is required.";
        } else if (useMobile && prefix && !phoneValue.startsWith(prefix)) {
            newErrors.phoneNumber = `Phone number must start with the prefix ${prefix}. (Example: ${prefix}${"X".repeat(
                mobileLength
            )})`;
        } else if (useMobile && phoneValue.length !== expectedLen) {
            const enteredLen = phoneValue.length;

            newErrors.phoneNumber =
                enteredLen < expectedLen
                    ? `Phone number is too short. Please enter ${expectedLen} digits (Example: ${prefix}${"X".repeat(
                          mobileLength
                      )})`
                    : `Phone number is too long. Please enter only ${expectedLen} digits (Example: ${prefix}${"X".repeat(
                          mobileLength
                      )})`;
        }

        if (newErrors.phoneNumber) {
            hideAlert();
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: newErrors.phoneNumber,
                mode: "Phone number",
            });
            setShowAlert("d-block");

            // Clear the alert after 5 seconds
            setTimeout(() => {
                setShowAlert("d-none"); // Hide the alert
                setAlertMessage({ success: true, message: "", mode: "" }); // Clear the message
            }, 5000);

            return; // Exit the function
        }

        if (inputRef.current.checkValidity()) {
            const key = useMobile ? "MOBILE_NUMBER" : "EMAIL_ID";
            const payLoad = {
                DIV_ID: "1",
                FUNCTION: "SendOTP",
                SEND_KEY: "123456",
                DATA: {
                    [key]: inputRef.current.value,
                    TYPE_NAME: useMobile ? "MOBILE" : "EMAIL",
                    CLIENT_ID: clientID,
                },
            };

            setLoading(true); // Start loading

            try {
                setLoading(true); // Start loading

                // Send OTP request using Axios
                const response = await axiosInstance.post(`${baseURL}/SendOTP`, payLoad, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

                if (response.data?.SUCCESS === "false") {
                    hideAlert();
                    setAlertStyle({
                        background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                    });
                    setAlertMessage({
                        success: false,
                        message: response.data?.MESSAGE,
                        mode: "Forgot password",
                    });
                    setShowAlert("d-block");
                    return;
                }

                // Store input value in cookies
                Cookies.set("wafaa-user-data-reset-key", JSON.stringify(inputRef.current.value));

                // Check if mobile mode is enabled
                if (useMobile) {
                    const smsText = appSettings?.SMS_SETTINGS?.OTP_MSG.replace("@OTP@", response.data?.DATA?.OTP);
                    const smsUrl = appSettings?.SMS_SETTINGS?.API_URL;

                    // Send SMS using Axios
                    const smsResponse = await axiosInstance.post(
                        smsUrl,
                        {
                            MobileNum: inputRef.current.value,
                            MessageText: smsText,
                        },
                        {
                            headers: {
                                "Content-Type": "application/json",
                            },
                        }
                    );

                    if (smsResponse.data?.data?.status === "SUCCESS") {
                        hideAlert();
                        setAlertStyle({
                            background: "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)",
                        });
                        setAlertMessage({
                            success: true,
                            message: "SMS sent successfully!",
                            mode: "Forgot password",
                        });
                        setShowAlert("d-block");
                        navigate("/forgot-password-otp");
                    } else {
                        throw new Error("Failed to send SMS. Please retry.");
                    }
                } else {
                    // Send SMS using Axios
                    const mailResponse = await axiosInstance.post(`${baseURL}/LoyaltyEssOtpEmailSender`, {
                        TemplateFile: "email/otpmail.json",
                        mailTo: [
                            {
                                MailId: inputRef.current.value,
                                MailName: "",
                            },
                        ],
                        paramData: [
                            {
                                OTP: response.data?.DATA?.OTP,
                            },
                        ],
                    });

                    if (mailResponse.data?.SUCCESS) {
                        hideAlert();
                        setAlertStyle({
                            background: "linear-gradient(to right, #4caf50 0%, #2e7d32 100%)",
                        });
                        setAlertMessage({
                            success: true,
                            message: "SMS sent successfully!",
                            mode: "Forgot password",
                        });
                        setShowAlert("d-block");
                        navigate("/forgot-password-otp");
                    } else {
                        throw new Error("Failed to send SMS. Please retry.");
                    }
                }
            } catch (error) {
                hideAlert();
                setAlertStyle({
                    background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
                });
                setAlertMessage({
                    success: false,
                    message: error.response?.data?.message || error.message || "An error occurred. Please try again.",
                    mode: "Forgot password",
                });
                setShowAlert("d-block");
            } finally {
                setLoading(false); // End loading
            }
        } else {
            hideAlert();
            setAlertStyle({
                background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
            });
            setAlertMessage({
                success: false,
                message: `${useMobile ? "Mobile number" : "Email"} input is not valid`,
                mode: "Forgot password",
            });
            setShowAlert("d-block");
            setLoading(false);
        }
    };

    return (
        <AnimatedPage>
            <div
                className="h-100 w-100 bg-wafaa-purple align-items-center text-white font-size-16 h-50"
                style={{
                    backgroundImage: `url(${bg_circles})`,
                }}
            >
                <Alert
                    showAlert={showAlert}
                    alertStyle={alertStyle}
                    alertMessage={alertMessage}
                    hideAlert={hideAlert}
                />
                <span className="d-block me-2" onClick={() => navigate(-1)}>
                    <img src={back_arrow} alt="Back button iOS" />
                </span>
                <div className="d-flex flex-column text-white px-4 pt-4 pb-5">
                    <div>
                        <p className="font-size-25 font-weight-bolder">
                            Forgot
                            <br />
                            Password?
                        </p>
                        <p className="mb-0 text-wafaa-white font-size-12">
                            Don't worry! It happens. Please enter your {useMobile ? "mobile number" : "email"} to
                            receive the OTP.
                        </p>
                    </div>
                    <input
                        className={`bg-wafaa-grey border-0 rounded-2 mt-4 ps-2 py-3 shadow-wafaa-btn-inset form-control ${
                            useMobile ? "d-block" : "d-none"
                        }`}
                        placeholder="Enter Mobile Number"
                        ref={mobileInput}
                        type="tel"
                        required
                    />
                    <div style={{ color: "white", fontSize: "12px", marginTop: "5px" }}>
                        {useMobile &&
                            `Phone number must start with the prefix ${appSettings?.SMS_SETTINGS?.ALLOWED_PREFIX}.`}
                    </div>
                    <input
                        className={`bg-wafaa-grey border-0 rounded-2 mt-4 ps-2 py-3 shadow-wafaa-btn-inset form-control ${
                            useMobile ? "d-none" : "d-block"
                        }`}
                        placeholder="Enter Email"
                        ref={emailInput}
                        type="email"
                        required
                    />
                    <button
                        type="button"
                        className="btn btn-link text-white font-size-12 mt-2"
                        onClick={() => setUseMobile(!useMobile)}
                    >
                        {useMobile ? "Use Email Instead" : "Use Mobile Number Instead"}
                    </button>
                </div>
            </div>
            <div className="position-absolute d-flex flex-column justify-content-end p-3 w-100 bottom-0 ">
                <div className="text-center">
                    <button
                        type="button"
                        onClick={sendPasswordReset}
                        className="w-100 btn btn-lg py-2-5 btn-primary bg-wafaa-grey border-wafaa-purple shadow-wafaa-btn font-size-12"
                        disabled={loading} // Disable button while loading
                        style={{ color: "#2c3691" }}
                    >
                        {loading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                            "Continue"
                        )}
                    </button>
                </div>
            </div>
        </AnimatedPage>
    );
}

export default ForgotPassword;
